import { useCallback, useRef } from 'react';

import { useAuthStore } from '../stores/auth.store';

const BASE_URL = import.meta.env.VITE_AGENT_URL ?? '/api/agent';

export interface SSEStreamOptions {
  /** Called for each text delta received */
  onDelta: (text: string) => void;
  /** Called when the stream completes */
  onDone: (fullText: string) => void;
  /** Called on error */
  onError: (error: string) => void;
  /** Max retries on network failure */
  maxRetries?: number;
}

/**
 * Custom hook for SSE streaming of agent responses.
 * Posts a message to the session endpoint and parses SSE events
 * (delta, done, error) from the response stream.
 */
export function useSSEStream(options: SSEStreamOptions) {
  const { onDelta, onDone, onError, maxRetries = 3 } = options;
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);

  const parseStream = useCallback(
    async (res: Response, controller: AbortController): Promise<void> => {
      if (!res.body) {
        onError('No response body received');
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            try {
              const event = JSON.parse(data) as {
                type: 'delta' | 'done' | 'error';
                text?: string;
                message?: string;
              };

              if (event.type === 'delta' && event.text) {
                fullText += event.text;
                onDelta(event.text);
              } else if (event.type === 'done') {
                onDone(fullText);
                return;
              } else if (event.type === 'error') {
                onError(event.message ?? 'Unknown streaming error');
                return;
              }
            } catch {
              // Non-JSON data line, skip
            }
          }
        }
      }

      // Stream ended without explicit done event
      if (fullText) {
        onDone(fullText);
      }
    },
    [onDelta, onDone, onError],
  );

  const send = useCallback(
    async (sessionId: string, content: string) => {
      const token = useAuthStore.getState().token;

      // Abort any in-flight stream
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      retryCountRef.current = 0;

      const attemptStream = async (): Promise<void> => {
        try {
          const res = await fetch(`${BASE_URL}/sessions/${sessionId}/messages`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ content }),
            signal: controller.signal,
          });

          if (!res.ok) {
            const errorBody = await res.json().catch(() => null);
            const message =
              errorBody?.error?.message ?? `Request failed with status ${res.status}`;
            onError(message);
            return;
          }

          await parseStream(res, controller);
        } catch (err: unknown) {
          if (controller.signal.aborted) return;

          retryCountRef.current += 1;
          if (retryCountRef.current <= maxRetries) {
            const delay = Math.pow(2, retryCountRef.current - 1) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            return attemptStream();
          }

          const message = err instanceof Error ? err.message : 'Network error';
          onError(message);
        }
      };

      await attemptStream();
    },
    [onDelta, onDone, onError, maxRetries, parseStream],
  );

  /**
   * Send a guest message — no auth, no session ID.
   * Sends message + conversation history to /guest/chat.
   */
  const sendGuest = useCallback(
    async (agentId: string, content: string, history: Array<{ role: 'user' | 'assistant'; content: string }>) => {
      // Abort any in-flight stream
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      retryCountRef.current = 0;

      const attemptStream = async (): Promise<void> => {
        try {
          const res = await fetch(`${BASE_URL}/guest/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: content, agentId, history }),
            signal: controller.signal,
          });

          if (!res.ok) {
            const errorBody = await res.json().catch(() => null);
            const message =
              errorBody?.error?.message ?? `Request failed with status ${res.status}`;
            onError(message);
            return;
          }

          await parseStream(res, controller);
        } catch (err: unknown) {
          if (controller.signal.aborted) return;

          retryCountRef.current += 1;
          if (retryCountRef.current <= maxRetries) {
            const delay = Math.pow(2, retryCountRef.current - 1) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            return attemptStream();
          }

          const message = err instanceof Error ? err.message : 'Network error';
          onError(message);
        }
      };

      await attemptStream();
    },
    [onDelta, onDone, onError, maxRetries, parseStream],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return { send, sendGuest, abort };
}
